import { EditorContent } from './types'

/** Maximum allowed size (in bytes) for the rich_text JSON sent to the server */
export const MAX_RICH_TEXT_BYTES = 4096

/**
 * Returns the byte size of the rich_text JSON that will be sent to the server.
 * Uses TextEncoder for accurate UTF-8 byte count.
 */
export const getRichTextByteSize = (content: EditorContent | null | undefined): number => {
  if (!content) return 0
  const json = JSON.stringify(content)
  return new TextEncoder().encode(json).length
}

/**
 * Extract plain text from TipTap EditorContent for contexts that don't support
 * rich text (PDF generation, search indexing, etc.).
 *
 * - Recursively walks the node tree
 * - Block-level nodes (paragraph, heading, etc.) get a trailing newline
 * - Text marks (bold, italic, color) are ignored — only text content is kept
 */
export const richTextToPlainText = (content: EditorContent | null | undefined): string => {
  if (!content?.content) return ''

  const BLOCK_NODES = new Set(['paragraph', 'heading', 'blockquote', 'codeBlock', 'listItem'])

  const extractNode = (node: any): string => {
    if (node.text !== undefined) return node.text
    if (!node.content) return ''
    const children = (node.content as any[]).map(extractNode).join('')
    return BLOCK_NODES.has(node.type) ? children + '\n' : children
  }

  return content.content.map(extractNode).join('').trimEnd()
}

/**
 * Convert a plain text string to TipTap EditorContent JSON.
 *
 * Handles:
 * - Empty / null / undefined → empty doc
 * - Newlines (\n) → separate paragraph nodes
 * - Each non-empty line → paragraph with a text node
 * - Empty lines → empty paragraph (preserves spacing)
 *
 * This allows legacy `text` fields to be rendered inside RichTextEditor
 * without any HTML parsing — plain text only, no formatting.
 */
export const textToEditorContent = (text: string | null | undefined): EditorContent => {
  if (!text?.trim()) {
    return { type: 'doc', content: [{ type: 'paragraph' }] }
  }

  const lines = text.split('\n')

  return {
    type: 'doc',
    content: lines.map((line) =>
      line ? { type: 'paragraph', content: [{ type: 'text', text: line }] } : { type: 'paragraph' }
    ),
  }
}

/**
 * Check if EditorContent is empty or has no meaningful content
 *
 * Returns true if:
 * - content is null or undefined
 * - content is an empty object
 * - content has no content array
 * - content array is empty
 * - content only contains empty paragraphs
 */
export const isEditorContentEmpty = (content: EditorContent | null | undefined): boolean => {
  if (!content) {
    return true
  }

  // Check if it's an empty object
  if (Object.keys(content).length === 0) {
    return true
  }

  // Check if there's no content array
  if (!content.content || !Array.isArray(content.content)) {
    return true
  }

  // Check if content array is empty
  if (content.content.length === 0) {
    return true
  }

  // Check if all content nodes are empty paragraphs
  const hasText = content.content.some((node) => {
    // If node has text directly
    if (node.text && node.text.trim()) {
      return true
    }

    // If node has content (nested), check recursively
    if (node.content && Array.isArray(node.content)) {
      return node.content.some((child) => child.text && child.text.trim())
    }

    return false
  })

  return !hasText
}
